import type { GraphQLClient } from "graphql-request";
import { DataUnavailableError, type JsonValue } from "@nexus/widget-sdk";
import type {
  CdnsaAccountWaterfallData,
  CdnsaCdnComparisonData,
  CdnsaDailyTrendData,
} from "@nexus/widgets";
import type { DataResolver, SourceSelection } from "@/lib/dashboard/resolverTypes";
import { normalizeReportMetrics, periodOf, reportParams } from "./reports";

export const CDNSA_METRICS = [
  { id: "cdnsa-infringements", label: "Distinct detections" },
  { id: "cdnsa-accounts", label: "Unique accounts detected" },
  { id: "cdnsa-ips", label: "Unique IPs detected" },
  { id: "cdnsa-accounts-daily-average", label: "Daily average accounts detected" },
  { id: "cdnsa-accounts-daily-max", label: "Maximum daily accounts detected" },
] as const;

export const CDNSA_TYPES = ["theft_for_restreaming", "cdn_leeching"] as const;
export const CDNSA_TYPE_OPTIONS = CDNSA_TYPES.map((value) => ({
  value,
  label: value === "cdn_leeching" ? "CDN leeching" : "Theft for restreaming",
}));
export const CDNSA_MEASURE_OPTIONS = [
  { value: "infringements", label: "Distinct detections" },
  { value: "accounts", label: "Unique accounts" },
  { value: "ips", label: "Unique IPs" },
];
const MEASURES = ["infringements", "accounts", "ips"] as const;
type Output = "metrics" | "cdn_comparison" | "daily";
type GqlReport = Parameters<typeof normalizeReportMetrics>[0] & {
  status: string;
  diagnostics: { output: string; status: string; tenant_ids: number[] }[];
  daily?: (CdnsaDailyTrendData["points"][number] &
    Partial<CdnsaAccountWaterfallData["points"][number]>)[];
  cdn_comparison?: CdnsaCdnComparisonData["points"];
};

function canonicalFilter(
  value: JsonValue | undefined,
  valid: (entry: string) => boolean,
): string[] | undefined {
  if (value === undefined) return undefined;
  if (
    !Array.isArray(value) ||
    value.length > 100 ||
    value.some((entry) => typeof entry !== "string" || !valid(entry.trim()))
  ) {
    throw new Error("Choose valid CDN/SA filters");
  }
  return [...new Set((value as string[]).map((entry) => entry.trim()))].sort();
}

export function cdnsaParams(params: Record<string, JsonValue>): Record<string, unknown> {
  const base = reportParams(params);
  if (params.from_ts !== undefined || params.to_ts !== undefined) {
    const from = params.from_ts;
    const to = params.to_ts;
    if (
      typeof from !== "string" ||
      typeof to !== "string" ||
      !/(?:Z|[+-]\d{2}:\d{2})$/i.test(from) ||
      !/(?:Z|[+-]\d{2}:\d{2})$/i.test(to) ||
      !Number.isFinite(Date.parse(from)) ||
      !Number.isFinite(Date.parse(to)) ||
      Date.parse(to) <= Date.parse(from) ||
      Date.parse(to) - Date.parse(from) > 3660 * 86400000
    ) {
      throw new Error("Choose a positive timezone-aware CDN/SA period of at most 3660 days");
    }
  }
  const metricIds = canonicalFilter(params.metric_ids, (id) =>
    CDNSA_METRICS.some((metric) => metric.id === id),
  );
  const types = canonicalFilter(params.infringement_types, (value) =>
    CDNSA_TYPES.some((type) => type === value),
  );
  const cdns = canonicalFilter(
    params.cdns,
    (value) => value.length <= 128 && /^[A-Za-z0-9][A-Za-z0-9_.-]*$/.test(value),
  );
  const measure = params.cdn_measure ?? "infringements";
  if (!MEASURES.some((value) => value === measure)) throw new Error("Choose a valid CDN measure");
  const limit = params.cdn_limit ?? 20;
  if (typeof limit !== "number" || !Number.isInteger(limit) || limit < 1 || limit > 100)
    throw new Error("Choose a CDN limit from 1 to 100");
  return {
    ...base,
    tenant_ids: [...new Set(base.tenant_ids as number[])].sort((left, right) => left - right),
    ...(metricIds === undefined ? {} : { metric_ids: metricIds }),
    ...(types === undefined ? {} : { infringement_types: types }),
    ...(cdns === undefined ? {} : { cdns }),
    cdn_measure: measure,
    cdn_limit: limit,
    include_output: params.include_output ?? true,
    include_comparison: params.include_comparison ?? false,
    include_new_accounts: params.include_new_accounts ?? false,
    include_freshness: params.include_freshness ?? true,
  };
}

const MESSAGES: Record<string, string> = {
  missing_mapping: "No CDN/SA source is configured for this tenant.",
  no_source_data: "No CDN/SA observations for this tenant.",
  invalid_identifiers:
    "Account or IP identifiers are unavailable because source data is malformed.",
  invalid_metadata: "CDN associations are unavailable because source metadata is malformed.",
  provider_error: "CDN/SA data could not be loaded.",
};

function checkAvailability(report: GqlReport, output: Output): void {
  const scope = report.diagnostics.find((diagnostic) => diagnostic.output === "scope");
  const status = scope?.status ?? report.status;
  // The tenants are chosen; their feed is just not there. Nothing to choose, nothing to retry.
  if (status === "missing_mapping" || status === "no_source_data")
    throw new DataUnavailableError(MESSAGES[status]);
  if (!["available", "empty", "partial"].includes(status))
    throw new Error(MESSAGES[status] ?? MESSAGES.provider_error);
  const problem = report.diagnostics.find((diagnostic) => diagnostic.output === output);
  if (problem) throw new Error(MESSAGES[problem.status] ?? MESSAGES.provider_error);
  if (
    output === "metrics" &&
    report.metrics?.length &&
    report.metrics.every((metric) => metric.value === null)
  ) {
    throw new Error(MESSAGES.invalid_identifiers);
  }
}

function selection(output: Output, accountFlow = false): SourceSelection {
  const field = output === "metrics" ? "metrics" : "points";
  return {
    mode: "select",
    fields: accountFlow
      ? [field]
      : [field, "freshness", ...(output === "daily" ? ["new_accounts"] : [])],
    ...(output === "metrics" ? { metrics: CDNSA_METRICS } : {}),
    toParams: ({ fields = [], metricIds, includeComparison }) => {
      if (includeComparison && output !== "metrics")
        throw new Error("CDN/SA chart comparisons are not supported");
      return {
        ...(output === "metrics" && metricIds !== undefined ? { metric_ids: [...metricIds] } : {}),
        ...(output === "metrics" ? { include_comparison: includeComparison ?? false } : {}),
        ...(output === "daily" && !accountFlow
          ? { include_new_accounts: fields.includes(field) && fields.includes("new_accounts") }
          : {}),
        include_output: fields.includes(field),
        include_freshness: fields.includes("freshness"),
      };
    },
  };
}

function source(output: Output, contract: string, label: string): DataResolver {
  const accountFlow = contract === "cdnsa-account-waterfall";
  const fields = {
    metrics:
      "id label value format unit occurred_at comparison_value @include(if:$include_comparison) delta_percent @include(if:$include_comparison) period { key from_ts to_ts label }",
    cdn_comparison: "cdn bucket_kind infringement_type value",
    daily: accountFlow
      ? "day accounts previous_accounts new_accounts lost_accounts"
      : "day theft_for_restreaming cdn_leeching new_accounts @include(if:$include_new_accounts)",
  };
  return {
    label,
    contract: { name: contract, version: 1 },
    selection: selection(output, accountFlow),
    report: {
      paramKeys: ["tenant_ids", "infringement_types", "cdns", "cdn_measure", "cdn_limit"],
      followScope: true,
    },
    buildParams: cdnsaParams,
    fetcher: async (params, client: GraphQLClient, signal) => {
      const document = `query CdnsaReport($tenant_ids:[Int!]!,$from_ts:String,$to_ts:String,$metric_ids:[String!],$infringement_types:[String!],$cdns:[String!],$cdn_measure:String,$cdn_limit:Int!,$include_output:Boolean!,$include_freshness:Boolean!${output === "metrics" ? ",$include_comparison:Boolean!" : output === "daily" && !accountFlow ? ",$include_new_accounts:Boolean!" : ""}) {
        cdnsa_report(tenant_ids:$tenant_ids,from_ts:$from_ts,to_ts:$to_ts,metric_ids:$metric_ids,infringement_types:$infringement_types,cdns:$cdns,cdn_measure:$cdn_measure,cdn_limit:$cdn_limit) {
          status diagnostics { output status tenant_ids }
          ${output} @include(if:$include_output) { ${fields[output]} }
          freshness @include(if:$include_freshness) { generated_at data_through source is_estimated }
        }
      }`;
      const raw = await client.request<{ cdnsa_report: GqlReport }>({
        document,
        variables: params,
        signal,
      });
      const report = raw.cdnsa_report;
      checkAvailability(report, output);
      if (accountFlow)
        return {
          ...periodOf(params),
          points: (report.daily ?? []).map((point) => ({
            day: point.day,
            accounts: point.accounts ?? null,
            previous_accounts: point.previous_accounts ?? null,
            new_accounts: point.new_accounts ?? null,
            lost_accounts: point.lost_accounts ?? null,
          })),
        } satisfies CdnsaAccountWaterfallData;
      const base = {
        status: report.status,
        diagnostics: report.diagnostics,
        freshness: normalizeReportMetrics(report).freshness,
      };
      if (output === "metrics") return { ...normalizeReportMetrics(report), ...base };
      if (output === "daily")
        return {
          ...base,
          ...periodOf(params),
          points: report.daily ?? [],
        } satisfies CdnsaDailyTrendData;
      return {
        ...base,
        points: report.cdn_comparison ?? [],
        measure: params.cdn_measure as CdnsaCdnComparisonData["measure"],
      } satisfies CdnsaCdnComparisonData;
    },
  };
}

export async function fetchCdnsaCdns(
  tenantIds: number[],
  client: GraphQLClient,
  signal?: AbortSignal,
): Promise<string[]> {
  const params = cdnsaParams({ tenant_ids: tenantIds });
  const raw = await client.request<{ cdnsa_report: { cdn_catalogue: string[] } }>({
    document: `query CdnsaCatalogue($tenant_ids:[Int!]!) { cdnsa_report(tenant_ids:$tenant_ids,metric_ids:[]) { cdn_catalogue } }`,
    variables: { tenant_ids: params.tenant_ids },
    signal,
  });
  return [...new Set(raw.cdnsa_report.cdn_catalogue)].sort();
}

export const cdnsaDataSources: Record<string, DataResolver> = {
  "cdnsa-metrics": source("metrics", "report-metrics", "CDN Security Analytics"),
  "cdnsa-cdn-comparison": source("cdn_comparison", "cdnsa-cdn-comparison", "CDN associations"),
  "cdnsa-daily-trend": source("daily", "cdnsa-daily-trend", "Daily observed detections"),
  "cdnsa-account-waterfall": source("daily", "cdnsa-account-waterfall", "Daily distinct accounts"),
};
